/**
 * The shelf as a list: one line a book, covers shrunk to a thumbnail.
 *
 * Why this exists at all. The grid is the right answer for browsing, and the
 * wrong one for counting: on a phone, six covers fill the screen, so a library
 * of thirty books is five screens of scrolling before anyone can say what is in
 * it or how far through it they are. A line is a fifth of the height and carries
 * more, because a line has room for words the caption of a card does not:
 * author, marks, share read, and the time left, all on the same rail.
 *
 * The two views show the same books in the same order, from the same query.
 * Switching is a layout change, never a different shelf.
 */

import { CheckIcon, Cover, Marks, ProgressBar, progressLabel, statusOf } from "./BookCard";
import { href } from "../lib/route";
import { t, useUiLang } from "../i18n";
import type { BookSummary } from "../lib/types";

export function BookRow({ book }: { book: BookSummary }) {
  useUiLang();
  const percent = book.progress?.percent ?? 0;
  const status = statusOf(book);
  const progress = progressLabel(book);

  return (
    <a
      href={href({ name: "book", id: book.id })}
      aria-label={
        book.author ? t("bookByAuthor", { title: book.title, author: book.author }) : book.title
      }
      className="group flex items-center gap-3 rounded-lg px-1 py-2.5 outline-offset-2 transition-colors duration-150 hover:bg-[var(--color-surface)] sm:gap-4 sm:px-2"
    >
      {/* 38px wide, so the 2:3 frame is 57px tall and the row stays under the
          height of a thumb. Small enough to be a marker, large enough that the
          artwork is still recognisable. */}
      <div className="w-fib34 shrink-0">
        <Cover book={book} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-baseline gap-3">
          <h3 className="truncate text-sm font-medium text-[var(--color-ink)] transition-colors duration-150 group-hover:text-[var(--color-accent)]">
            {book.title}
          </h3>
          {progress && (
            <span
              className={
                "ml-auto flex shrink-0 items-center gap-1 font-[family-name:var(--font-mono)] text-2xs " +
                (status === "finished"
                  ? "text-[var(--color-accent)]"
                  : "text-[var(--color-muted)]")
              }
            >
              {/* A finished book gets the tick as well as the word: scanning a
                  column of thirty rows, the eye finds a shape long before it
                  reads a label. */}
              {status === "finished" && <CheckIcon className="h-3 w-3" />}
              {progress}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <p className="truncate text-xs text-[var(--color-muted)]">{book.author}</p>
          <span className="ml-auto shrink-0">
            <Marks book={book} />
          </span>
        </div>

        {/* The bar is the whole point of the list: thirty of them stacked is a
            reading history that can be read in one look. A book never opened
            keeps its empty track rather than nothing, so the column lines up. */}
        <ProgressBar percent={percent} />
      </div>
    </a>
  );
}

/** Same height as a row, so the list does not jump when the books arrive. */
export function BookRowSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 px-1 py-2.5 sm:gap-4 sm:px-2" aria-hidden="true">
      <div className="aspect-[2/3] w-fib34 shrink-0 rounded-md bg-[var(--color-surface)]" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-3 w-2/3 rounded bg-[var(--color-surface)]" />
        <div className="h-2.5 w-1/3 rounded bg-[var(--color-surface)]" />
        <div className="h-fib3 w-full rounded bg-[var(--color-surface)]" />
      </div>
    </div>
  );
}
